// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';
import { Package, Truck, CheckCircle, Clock, XCircle, ChevronRight, MapPin } from 'lucide-react';

const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

const ORDER_STATUSES = [
  { key: 'PENDING',           label: 'Order Placed',      icon: Clock,         color: '#F59E0B' },
  { key: 'CONFIRMED',         label: 'Confirmed',          icon: CheckCircle,   color: '#3B82F6' },
  { key: 'PREPARING',         label: 'Being Prepared',     icon: Package,       color: '#8B5CF6' },
  { key: 'READY',             label: 'Ready',              icon: Package,       color: '#16A34A' },
  { key: 'OUT_FOR_DELIVERY',  label: 'Out for Delivery',   icon: Truck,         color: '#F97316' },
  { key: 'DELIVERED',         label: 'Delivered',          icon: CheckCircle,   color: '#16A34A' },
  { key: 'PICKED_UP',         label: 'Picked Up',          icon: CheckCircle,   color: '#16A34A' },
  { key: 'CANCELLED',         label: 'Cancelled',          icon: XCircle,       color: '#EF4444' },
];

function getStatusStep(status) {
  const deliveryFlow = ['PENDING','CONFIRMED','PREPARING','READY','OUT_FOR_DELIVERY','DELIVERED'];
  const pickupFlow   = ['PENDING','CONFIRMED','PREPARING','READY','PICKED_UP'];
  const flow = status === 'PICKED_UP' || status === 'READY' ? pickupFlow : deliveryFlow;
  return { flow, step: flow.indexOf(status) };
}

function statusColor(status) {
  const s = ORDER_STATUSES.find(o => o.key === status);
  return s?.color || '#94A3B8';
}

function statusLabel(status) {
  const s = ORDER_STATUSES.find(o => o.key === status);
  return s?.label || status;
}

function OrderCard({ order, onClick }) {
  const color = statusColor(order.order_status);
  const isTerminal = ['DELIVERED','PICKED_UP','CANCELLED'].includes(order.order_status);

  return (
    <div
      className="card"
      onClick={onClick}
      style={{ padding: 20, cursor: 'pointer', marginBottom: 12, transition: 'box-shadow 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(22,163,74,0.12)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#14532D' }}>Order #{order.order_number}</div>
          <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>
            {new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            {' · '}{order.fulfillment_type === 'DELIVERY' ? '🚚 Delivery' : '📦 Pickup'}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: color + '18', color,
          }}>
            {statusLabel(order.order_status)}
          </span>
          <ChevronRight size={16} color="#94A3B8" />
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748B' }}>
        <span>{order.grocery_order_items?.length || 0} items</span>
        <span style={{ fontWeight: 700, color: '#14532D' }}>{fmt(order.total_cents)}</span>
      </div>
      {!isTerminal && (
        <div style={{ marginTop: 10 }}>
          {(() => {
            const { flow, step } = getStatusStep(order.order_status);
            const pct = Math.round(((step + 1) / flow.length) * 100);
            return (
              <div style={{ height: 4, background: '#F0FDF4', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.6s ease' }} />
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

function OrderDetail({ order }) {
  const color = statusColor(order.order_status);
  const isDelivery = order.fulfillment_type === 'DELIVERY';
  const isTerminal = ['DELIVERED','PICKED_UP','CANCELLED'].includes(order.order_status);
  const { flow, step } = getStatusStep(order.order_status);

  return (
    <div>
      {/* Status hero */}
      <div style={{
        background: `linear-gradient(135deg, ${color}18, ${color}08)`,
        border: `1px solid ${color}30`,
        borderRadius: 16, padding: 24, marginBottom: 24, textAlign: 'center',
      }}>
        <div style={{ fontSize: 42, marginBottom: 8 }}>
          {order.order_status === 'DELIVERED' || order.order_status === 'PICKED_UP' ? '✅' :
           order.order_status === 'CANCELLED' ? '❌' :
           order.order_status === 'OUT_FOR_DELIVERY' ? '🚚' : '📦'}
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, color: '#14532D', marginBottom: 4 }}>
          {statusLabel(order.order_status)}
        </div>
        <div style={{ fontSize: 13, color: '#64748B' }}>Order #{order.order_number}</div>
      </div>

      {/* Progress tracker */}
      {!isTerminal && (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#14532D', marginBottom: 16 }}>
            {isDelivery ? 'Delivery Progress' : 'Order Progress'}
          </h3>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 0 }}>
            {flow.map((s, i) => {
              const done  = i <= step;
              const active = i === step;
              const sc = statusColor(s);
              const sl = statusLabel(s);
              return (
                <div key={s} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    {i > 0 && <div style={{ flex: 1, height: 2, background: done ? '#16A34A' : '#E2E8F0' }} />}
                    <div style={{
                      width: active ? 28 : 22, height: active ? 28 : 22,
                      borderRadius: '50%', flexShrink: 0,
                      background: done ? '#16A34A' : '#E2E8F0',
                      border: active ? '3px solid #15803D' : 'none',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.3s',
                    }}>
                      {done && <CheckCircle size={active ? 16 : 13} color="#fff" />}
                    </div>
                    {i < flow.length - 1 && <div style={{ flex: 1, height: 2, background: i < step ? '#16A34A' : '#E2E8F0' }} />}
                  </div>
                  <div style={{ fontSize: 10, color: done ? '#14532D' : '#94A3B8', fontWeight: active ? 700 : 500, marginTop: 6, textAlign: 'center', lineHeight: 1.3 }}>
                    {sl}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Delivery address */}
      {isDelivery && order.delivery_address && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <MapPin size={16} color="#16A34A" style={{ flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 2 }}>DELIVERY ADDRESS</div>
              <div style={{ fontSize: 14, color: '#14532D', fontWeight: 600 }}>{order.delivery_address.full_name}</div>
              <div style={{ fontSize: 13, color: '#64748B' }}>
                {order.delivery_address.address_line1}
                {order.delivery_address.address_line2 && `, ${order.delivery_address.address_line2}`}
              </div>
              <div style={{ fontSize: 13, color: '#64748B' }}>
                {order.delivery_address.city}, {order.delivery_address.state} {order.delivery_address.zip}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Items */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#14532D', marginBottom: 14 }}>Items Ordered</h3>
        {(order.grocery_order_items || []).map(item => (
          <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #F0FDF4', fontSize: 13 }}>
            <div>
              <span style={{ fontWeight: 600, color: '#14532D' }}>{item.product_name}</span>
              <span style={{ color: '#94A3B8' }}> × {item.quantity}</span>
            </div>
            <span style={{ fontWeight: 700, color: '#14532D' }}>{fmt(item.total_cents)}</span>
          </div>
        ))}
      </div>

      {/* Order totals */}
      <div className="card" style={{ padding: 20, marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: '#14532D', marginBottom: 14 }}>Order Total</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B' }}>
            <span>Subtotal</span><span>{fmt(order.subtotal_cents)}</span>
          </div>
          {order.discount_cents > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#16A34A', fontWeight: 600 }}>
              <span>Discount</span><span>−{fmt(order.discount_cents)}</span>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B' }}>
            <span>{order.fulfillment_type === 'PICKUP' ? 'Pickup' : 'Delivery'}</span>
            <span style={{ color: order.delivery_fee_cents === 0 ? '#16A34A' : undefined }}>
              {order.delivery_fee_cents === 0 ? 'Free' : fmt(order.delivery_fee_cents)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', color: '#64748B' }}>
            <span>Tax</span><span>{fmt(order.tax_cents)}</span>
          </div>
          <div style={{ height: 1, background: '#F0FDF4' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 16, fontWeight: 900, color: '#14532D' }}>
            <span>Total</span><span>{fmt(order.total_cents)}</span>
          </div>
          {order.loyalty_points_earned > 0 && (
            <div style={{ background: '#F0FDF4', borderRadius: 8, padding: '8px 12px', marginTop: 4, fontSize: 12, color: '#16A34A', fontWeight: 600 }}>
              ⭐ +{order.loyalty_points_earned} loyalty points earned
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#64748B', marginBottom: 4 }}>ORDER NOTES</div>
          <div style={{ fontSize: 13, color: '#374151' }}>{order.notes}</div>
        </div>
      )}
    </div>
  );
}

export default function OrderTrackingPage() {
  const { orderId } = useParams();
  const navigate    = useNavigate();
  const { customer } = useAuth();

  const [orders, setOrders]   = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    fetchOrders();
  }, [customer, orderId]);

  const fetchOrders = async () => {
    if (!customer?.id) return;
    setLoading(true);

    const { data } = await supabase
      .from('grocery_orders')
      .select(`
        *,
        grocery_order_items (
          id, product_name, quantity, unit_price_cents, total_cents
        )
      `)
      .eq('customer_id', customer.id)
      .order('created_at', { ascending: false });

    setOrders(data || []);

    if (orderId) {
      const match = (data || []).find(o => o.id === orderId);
      setSelected(match || null);
    }

    setLoading(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="page-container" style={{ paddingTop: 32, paddingBottom: 48 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        {selected && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => { setSelected(null); navigate('/orders'); }}
          >
            ← Back
          </button>
        )}
        <h1 style={{ fontSize: 26, fontWeight: 900, color: '#14532D', margin: 0 }}>
          {selected ? `Order #${selected.order_number}` : 'My Orders'}
        </h1>
      </div>

      {selected ? (
        <OrderDetail order={selected} />
      ) : (
        <>
          {orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '64px 0', color: '#94A3B8' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>📦</div>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8, color: '#64748B' }}>No orders yet</div>
              <div style={{ fontSize: 14, marginBottom: 24 }}>Your order history will appear here.</div>
              <Link to="/products" style={{ textDecoration: 'none' }}>
                <button className="btn btn-primary">Start Shopping</button>
              </Link>
            </div>
          ) : (
            <div>
              {orders.map(order => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onClick={() => { setSelected(order); navigate(`/orders/${order.id}`); }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
